import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ExternalLink,
  FileInput,
  Plus,
  QrCode,
  RefreshCw,
  ShoppingBag,
  Trash2,
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
  Phone,
  Radio,
  Megaphone,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { Button } from "@/components/Button";
import { Card, SectionHeader } from "@/components/Card";
import { Modal } from "@/components/Modal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { LoadErrorState } from "@/components/ListStates";
import { useActiveAccount } from "@/hooks/useActiveAccount";
import {
  CatalogProductInput,
  NativeFlowInput,
  WhatsappAsset,
  whatsappPlatformService as service,
} from "@/services/whatsapp-platform.service";

import CatalogProducts from "./CatalogProducts";

const input =
  "w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/15";
const newFlow: NativeFlowInput = {
  name: "",
  title: "How can we help?",
  description: "Leave your details and our team will get in touch.",
  category: "LEAD_GENERATION",
  fields: [
    { key: "customer_name", label: "Your name", type: "text", required: true },
    { key: "email", label: "Email address", type: "email", required: false },
  ],
};

export default function WhatsappCatalog() {
  const { accounts, active, loading: accountsLoading } = useActiveAccount();
  const location = useLocation();
  const [prefill] = useState<Partial<CatalogProductInput> | null>(
    () => (location.state as { prefillProduct?: Partial<CatalogProductInput> } | null)?.prefillProduct ?? null,
  );
  const [prefillPending, setPrefillPending] = useState(!!prefill);
  // The product list remounts per account and hides while loading, so the draft opens only once this account's list is in.
  const [loadedFor, setLoadedFor] = useState('');
  const prefillUsed = useCallback(() => {
    setPrefillPending(false);
    // Drop the hand-off so a refresh doesn't reopen the draft. Straight on the
    // history entry: a router navigate would remount the page and close it.
    try { window.history.replaceState({ ...window.history.state, usr: null }, ""); } catch { /* ignore */ }
  }, []);
  const [selected, setSelected] = useState("");
  const accountUid =
    selected || active?.accountUid || accounts[0]?.accountUid || "";
  const [tab, setTab] = useState<"catalog" | "flows" | "qr" | "features">(
    "catalog",
  );
  const [assets, setAssets] = useState<WhatsappAsset[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [flow, setFlow] = useState<NativeFlowInput>(newFlow);
  const [message, setMessage] = useState("Hello, I would like to know more.");
  const [publishing, setPublishing] = useState<WhatsappAsset | null>(null);
  const [preview, setPreview] = useState("");
  const [cart, setCart] = useState(false);
  const [visible, setVisible] = useState(false);

  const load = useCallback(async () => {
    if (!accountUid) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      setAssets(await service.list(accountUid));
    } catch {
      setAssets([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
      setLoadedFor(accountUid);
    }
  }, [accountUid]);
  useEffect(() => {
    setAssets([]);
    setErrors({});
    void load();
  }, [load]);
  const commerce = assets.find(
    (a) => a.kind === "commerce" && a.status === "synced" && !a.data.appliedTo,
  );
  useEffect(() => {
    setCart(!!commerce?.data.is_cart_enabled);
    setVisible(!!commerce?.data.is_catalog_visible);
  }, [commerce]);
  const sync = async () => {
    setBusy(true);
    try {
      const result = await service.sync(accountUid);
      setAssets(result.assets);
      setErrors(result.errors);
      if (!Object.keys(result.errors).length)
        toast.success("WhatsApp tools synced");
    } catch {
      /* API handles error */
    } finally {
      setBusy(false);
    }
  };
  const saveCommerce = async () => {
    setBusy(true);
    try {
      await service.commerce(accountUid, {
        is_cart_enabled: cart,
        is_catalog_visible: visible,
      });
      await load();
      toast.success("Catalog settings updated on WhatsApp");
    } catch {
      /* API handles error */
    } finally {
      setBusy(false);
    }
  };
  const createFlow = async () => {
    setBusy(true);
    try {
      const result = await service.createFlow(accountUid, flow);
      await load();
      setFlowOpen(false);
      toast.success(
        result.data.validation_errors?.length
          ? "Draft saved. Review Meta validation errors."
          : "Flow draft created on WhatsApp",
      );
    } catch {
      /* API handles error */
    } finally {
      setBusy(false);
    }
  };
  const createQr = async () => {
    setBusy(true);
    try {
      await service.createQr(accountUid, message);
      await load();
      setQrOpen(false);
      toast.success("WhatsApp QR link created");
    } catch {
      /* API handles error */
    } finally {
      setBusy(false);
    }
  };
  const publish = async () => {
    if (!publishing) return;
    setBusy(true);
    try {
      await service.publishFlow(accountUid, publishing.assetUid);
      await load();
      setPublishing(null);
      toast.success("Flow published");
    } catch {
      /* API handles error */
    } finally {
      setBusy(false);
    }
  };
  const rows = (kind: WhatsappAsset["kind"]) =>
    assets.filter((a) => a.kind === kind && !a.data.appliedTo);

  return (
    <>
      <WhatsAppHeader
        title="Catalog & tools"
        section="catalog"
        description="Bring your products, forms and customer entry points into WhatsApp."
        actions={
          <Button
            variant="secondary"
            onClick={sync}
            disabled={!accountUid}
            loading={busy}
            iconLeft={<RefreshCw size={15} />}
          >
            Sync from Meta
          </Button>
        }
      />
      <div className="space-y-6 p-4 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary-soft p-2.5 text-primary">
              <ShoppingBag size={20} />
            </div>
            <div>
              <div className="text-sm font-semibold">
                Your business on WhatsApp
              </div>
              <p className="text-xs text-text-muted">
                Catalog, cart and forms are saved for the selected account.
              </p>
            </div>
          </div>
          <select
            aria-label="WhatsApp account"
            className={clsx(input, "sm:!w-auto")}
            value={accountUid}
            onChange={(e) => setSelected(e.target.value)}
          >
            {accounts.map((a) => (
              <option key={a.accountUid} value={a.accountUid}>
                {a.businessName} · {a.displayPhoneNumber}
              </option>
            ))}
          </select>
        </div>
        {!accountUid && !accountsLoading && (
          <Card>
            <p className="text-sm text-text-muted">
              Connect a WhatsApp Business account to use these tools.
            </p>
            <Link
              to="/whatsapp-accounts"
              className="mt-3 inline-flex text-sm font-semibold text-primary"
            >
              Connect account →
            </Link>
          </Card>
        )}
        <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-surface-alt p-1">
          {(
            [
              { key: "catalog", label: "Catalog", icon: ShoppingBag },
              { key: "flows", label: "WhatsApp forms", icon: FileInput },
              { key: "qr", label: "QR & chat links", icon: QrCode },
              { key: "features", label: "More capabilities", icon: Radio },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                "flex shrink-0 items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium",
                tab === t.key
                  ? "bg-surface text-primary shadow-sm"
                  : "text-text-muted",
              )}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>
        {Object.keys(errors).length > 0 && (
          <div className="rounded-xl border border-warning/30 bg-warning-soft p-4 text-sm">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <AlertCircle size={17} />
              Some tools need attention
            </div>
            {Object.entries(errors).map(([key, error]) => (
              <p key={key} className="mt-1 text-xs">
                <strong className="capitalize">{key}:</strong> {error}
              </p>
            ))}
          </div>
        )}
        {loading ? (
          <div className="skeleton h-72 rounded-2xl" />
        ) : loadFailed && tab !== "features" ? (
          <LoadErrorState what="your WhatsApp tools" onRetry={() => void load()} />
        ) : tab === "catalog" ? (
          <>
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <CatalogProducts key={accountUid} accountUid={accountUid} assets={assets} onChanged={load} prefill={prefillPending && accountUid && loadedFor === accountUid ? prefill : null} onPrefillUsed={prefillUsed} />
              <Card className="h-fit">
                <SectionHeader
                  title="Shopping experience"
                  subtitle={
                    commerce?.lastSyncedAt
                      ? `Synced ${new Date(commerce.lastSyncedAt).toLocaleDateString()}`
                      : "Sync first to read the current settings."
                  }
                />
                <label className="flex items-center justify-between gap-4 border-b border-border py-4 text-sm">
                  <span>
                    <strong className="block font-medium">Show catalog</strong>
                    <span className="text-xs text-text-muted">
                      Let customers discover your products.
                    </span>
                  </span>
                  <input
                    aria-label="Show catalog"
                    type="checkbox"
                    checked={visible}
                    onChange={(e) => setVisible(e.target.checked)}
                    disabled={!commerce || busy}
                    className="h-4 w-4 accent-primary"
                  />
                </label>
                <label className="flex items-center justify-between gap-4 py-4 text-sm">
                  <span>
                    <strong className="block font-medium">Shopping cart</strong>
                    <span className="text-xs text-text-muted">
                      Accept product orders inside the chat.
                    </span>
                  </span>
                  <input
                    aria-label="Shopping cart"
                    type="checkbox"
                    checked={cart}
                    onChange={(e) => setCart(e.target.checked)}
                    disabled={!commerce || busy}
                    className="h-4 w-4 accent-primary"
                  />
                </label>
                <Button
                  className="mt-4 w-full"
                  onClick={saveCommerce}
                  disabled={
                    !commerce ||
                    busy ||
                    (cart === commerce.data.is_cart_enabled &&
                      visible === commerce.data.is_catalog_visible)
                  }
                >
                  Save to WhatsApp
                </Button>
              </Card>
            </div>
          </>
        ) : tab === "flows" ? (
          <Card>
            <SectionHeader
              title="Forms that open inside WhatsApp"
              subtitle="Collect enquiries, booking details and feedback without leaving the conversation."
              icon={<FileInput size={19} />}
              action={
                <Button
                  disabled={!accountUid}
                  onClick={() => {
                    setFlow(newFlow);
                    setFlowOpen(true);
                  }}
                  iconLeft={<Plus size={15} />}
                >
                  Create form
                </Button>
              }
            />
            <div className="grid gap-4 lg:grid-cols-2">
              {rows("flow").map((f) => (
                <div
                  key={f.assetUid}
                  className="rounded-xl border border-border p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">{f.name}</h3>
                      <p className="mt-1 text-xs text-text-muted">
                        {(f.data.categories ?? [f.data.category])
                          .filter(Boolean)
                          .join(" · ")
                          .replace(/_/g, " ")}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        "rounded-full px-2.5 py-1 text-[11px] font-semibold",
                        f.status === "PUBLISHED"
                          ? "bg-success-soft text-success"
                          : "bg-surface-alt text-text-muted",
                      )}
                    >
                      {f.status}
                    </span>
                  </div>
                  {f.syncError && (
                    <p className="mt-3 text-xs text-danger">{f.syncError}</p>
                  )}
                  {f.data.validation_errors?.map((e: any, i: number) => (
                    <p key={i} className="mt-2 text-xs text-danger">
                      {e.message}
                    </p>
                  ))}
                  <div className="mt-5 flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!f.metaId}
                      onClick={async () => {
                        try {
                          const r = await service.previewFlow(
                            accountUid,
                            f.assetUid,
                          );
                          setPreview(r.preview.preview_url);
                        } catch {
                          /* API handles error */
                        }
                      }}
                    >
                      Preview
                    </Button>
                    {f.status === "DRAFT" && (
                      <Button
                        size="sm"
                        disabled={!!f.data.validation_errors?.length}
                        onClick={() => setPublishing(f)}
                      >
                        Publish
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {!rows("flow").length && (
              <EmptyState
                icon={<FileInput size={28} />}
                title="No forms yet"
                description="WhatsApp forms open inside the chat to collect enquiries, booking details or feedback. Create a draft, preview it, then publish it when it's ready."
                action={
                  <Button
                    disabled={!accountUid}
                    onClick={() => {
                      setFlow(newFlow);
                      setFlowOpen(true);
                    }}
                    iconLeft={<Plus size={15} />}
                  >
                    Create form
                  </Button>
                }
              />
            )}
          </Card>
        ) : tab === "qr" ? (
          <Card>
            <SectionHeader
              title="A direct line to your business"
              subtitle="Print a QR code or share a link that starts the conversation."
              icon={<QrCode size={19} />}
              action={
                <Button
                  disabled={!accountUid}
                  onClick={() => setQrOpen(true)}
                  iconLeft={<Plus size={15} />}
                >
                  Create QR link
                </Button>
              }
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rows("qr").map((q) => (
                <div
                  key={q.assetUid}
                  className="rounded-xl border border-border p-5"
                >
                  {q.data.imageUrl && (
                    <img
                      alt="WhatsApp chat QR code"
                      src={q.data.imageUrl}
                      className="mx-auto mb-4 h-36 w-36 rounded-xl bg-white p-2"
                    />
                  )}
                  <p className="text-sm font-medium">
                    {q.data.prefilled_message ?? q.name}
                  </p>
                  {q.syncError && (
                    <p className="mt-2 text-xs text-danger">{q.syncError}</p>
                  )}
                  {q.data.deep_link_url && (
                    <a
                      href={q.data.deep_link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex items-center gap-1 text-xs text-primary"
                    >
                      Open WhatsApp link <ArrowUpRight size={13} />
                    </a>
                  )}
                </div>
              ))}
            </div>
            {!rows("qr").length && (
              <EmptyState
                icon={<QrCode size={28} />}
                title="No QR links yet"
                description="A QR code or chat link opens WhatsApp with your welcome message already typed, so customers can start a conversation in one tap. Print it or share the link anywhere."
                action={
                  <Button
                    disabled={!accountUid}
                    onClick={() => setQrOpen(true)}
                    iconLeft={<Plus size={15} />}
                  >
                    Create QR link
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            <Feature
              icon={<Megaphone />}
              title="Ads in WhatsApp Status"
              detail="Reach customers between Status updates using Meta Marketing API v26. Connect an ad account and select a WhatsApp identity to create a paused campaign."
              href="/whatsapp/ads"
              label="Open Status ads"
            />
            <Feature
              icon={<Phone />}
              title="WhatsApp calling"
              detail="Voice and video calling require an eligible business number, customer call permissions and a WebRTC or SIP media connection. Calling is not configured in this workspace yet."
              external="https://developers.facebook.com/documentation/business-messaging/whatsapp/calling"
            />
            <Feature
              icon={<Radio />}
              title="Channels & group conversations"
              detail="Group messaging has its own eligibility requirements, including an Official Business Account. Channel publishing is not part of the Cloud API integration here; manage your Channel in WhatsApp."
              external="https://developers.facebook.com/documentation/business-messaging/whatsapp/groups"
            />
            <Feature
              icon={<CheckCircle2 />}
              title="AI & scripted automation"
              detail="Generate a bot from your business brief, enhance instructions, or build a guided conversation. AI uses the provider and model configured for your company."
              href="/leads/bot-flows"
              label="Open automation"
            />
          </div>
        )}
      </div>
      <Modal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        title="Create a WhatsApp QR link"
      >
        <label className="mb-2 block text-sm font-medium">
          Message customers start with
        </label>
        <textarea
          className={input}
          rows={3}
          maxLength={140}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <p className="mt-2 text-xs text-text-muted">
          {message.length}/140 characters
        </p>
        <Button
          className="mt-5"
          disabled={!message.trim()}
          loading={busy}
          onClick={createQr}
        >
          Create QR link
        </Button>
      </Modal>
      <Modal
        open={flowOpen}
        onClose={() => setFlowOpen(false)}
        title="Create a WhatsApp form"
        widthClass="max-w-3xl"
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5 text-xs font-medium">
              Form name
              <input
                className={input}
                value={flow.name}
                onChange={(e) => setFlow({ ...flow, name: e.target.value })}
                placeholder="Customer enquiry"
                maxLength={200}
              />
            </label>
            <label className="space-y-1.5 text-xs font-medium">
              Purpose
              <select
                className={input}
                value={flow.category}
                onChange={(e) => setFlow({ ...flow, category: e.target.value })}
              >
                {[
                  "LEAD_GENERATION",
                  "APPOINTMENT_BOOKING",
                  "CONTACT_US",
                  "CUSTOMER_SUPPORT",
                  "SURVEY",
                  "SIGN_UP",
                  "OTHER",
                ].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="block space-y-1.5 text-xs font-medium">
            Screen title
            <input
              className={input}
              maxLength={80}
              value={flow.title}
              onChange={(e) => setFlow({ ...flow, title: e.target.value })}
            />
          </label>
          <label className="block space-y-1.5 text-xs font-medium">
            Introduction
            <textarea
              className={input}
              maxLength={1024}
              value={flow.description}
              onChange={(e) =>
                setFlow({ ...flow, description: e.target.value })
              }
            />
          </label>
          {flow.fields.map((f, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 rounded-xl bg-surface-alt p-3"
            >
              <input
                aria-label={`Field ${i + 1} label`}
                className={input}
                value={f.label}
                maxLength={80}
                onChange={(e) =>
                  setFlow({
                    ...flow,
                    fields: flow.fields.map((v, n) =>
                      n === i ? { ...v, label: e.target.value } : v,
                    ),
                  })
                }
              />
              <select
                aria-label={`Field ${i + 1} type`}
                className={input}
                value={f.type}
                onChange={(e) =>
                  setFlow({
                    ...flow,
                    fields: flow.fields.map((v, n) =>
                      n === i ? { ...v, type: e.target.value } : v,
                    ),
                  })
                }
              >
                {["text", "email", "phone", "number"].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <button
                aria-label={`Remove field ${i + 1}`}
                disabled={flow.fields.length === 1}
                onClick={() =>
                  setFlow({
                    ...flow,
                    fields: flow.fields.filter((_, n) => n !== i),
                  })
                }
                className="p-2 text-text-muted hover:text-danger disabled:opacity-30"
              >
                <Trash2 size={16} />
              </button>
              <label className="col-span-3 flex items-center gap-2 text-xs text-text-muted">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={(e) =>
                    setFlow({
                      ...flow,
                      fields: flow.fields.map((v, n) =>
                        n === i ? { ...v, required: e.target.checked } : v,
                      ),
                    })
                  }
                />
                Required
              </label>
            </div>
          ))}
          <div className="flex justify-between gap-3">
            <Button
              variant="secondary"
              disabled={flow.fields.length >= 8}
              onClick={() =>
                setFlow({
                  ...flow,
                  fields: [
                    ...flow.fields,
                    {
                      key: `field_${Date.now()}`,
                      label: "New field",
                      type: "text",
                      required: false,
                    },
                  ],
                })
              }
            >
              Add field
            </Button>
            <Button
              loading={busy}
              disabled={
                flow.name.trim().length < 3 ||
                !flow.title.trim() ||
                flow.fields.some((f) => !f.label.trim())
              }
              onClick={createFlow}
            >
              Create draft on WhatsApp
            </Button>
          </div>
          <p className="text-xs text-text-muted">
            Starts as a draft. Publishing is a separate step after you preview
            and check the form.
          </p>
        </div>
      </Modal>
      <Modal
        open={!!preview}
        onClose={() => setPreview("")}
        title="WhatsApp form preview"
        widthClass="max-w-lg"
      >
        {preview && (
          <iframe
            title="Meta WhatsApp form preview"
            src={preview}
            className="h-[650px] w-full border-0"
          />
        )}
      </Modal>
      <ConfirmDialog
        open={!!publishing}
        onCancel={() => {
          if (!busy) setPublishing(null);
        }}
        onConfirm={() => {
          if (!busy) void publish();
        }}
        title="Publish this WhatsApp form?"
        message="Published WhatsApp forms cannot be edited. Preview and check every field first; future changes require a new form."
      />
    </>
  );
}

function Feature({
  icon,
  title,
  detail,
  href,
  external,
  label,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  href?: string;
  external?: string;
  label?: string;
}) {
  return (
    <Card>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
        {icon}
      </div>
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-text-muted">{detail}</p>
      {href && (
        <Link
          to={href}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary"
        >
          {label} <ArrowUpRight size={15} />
        </Link>
      )}
      {external && (
        <a
          href={external}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          Requirements & documentation <ExternalLink size={14} />
        </a>
      )}
    </Card>
  );
}
